import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useConfigCredentials } from '@/hooks/useCredentialReferences';
import { cn } from '@/lib/utils';

/**
 * The placeholders gateways made for secrets found in device configs that no
 * credential held (website-backend#81). They let a stored config carry a
 * reference instead of the secret, and they rebuild the config on restore.
 * Nothing can use one: they are never offered for a connection, poll or
 * operation, and the values never leave the gateways.
 */
export default function ConfigCredentialsSection() {
  const [open, setOpen] = useState(false);
  const { data: credentials, isLoading } = useConfigCredentials(true);
  const count = credentials?.length ?? 0;
  if (!isLoading && count === 0) return null;

  return (
    <section className="mt-6">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-foreground">
          <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} />
          From device configs
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal">{isLoading ? '…' : count}</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <p className="mt-2 text-xs text-muted-foreground">
            Secrets a gateway found in a device’s configuration that no credential held. They keep
            stored configs free of the values and rebuild them on restore. Witina never uses them to
            connect, poll or change anything, and their values stay on your gateways.
          </p>
          <ul className="mt-2 rounded-md border border-border-subtle">
            {(credentials ?? []).map((c) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-2 text-sm last:border-b-0"
              >
                <div className="min-w-0">
                  <span className="truncate">{c.name}</span>
                  {c.raw ? (
                    <span
                      className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      title="A hash or encrypted value as the device wrote it: it rebuilds the config, and signs nothing in."
                    >
                      hash as found
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {c.deviceId ? (
                    <Link to={`/devices/${c.deviceId}`} className="hover:text-foreground hover:underline">
                      {c.deviceName ?? 'device'}
                    </Link>
                  ) : null}
                  <span>
                    {c.configs} stored config{c.configs === 1 ? '' : 's'}
                    {c.devices > 1 ? ` on ${String(c.devices)} devices` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
